import { FC, HTMLAttributes, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Typography } from 'antd';
import cn from 'classnames';

import { RootState } from 'app/appStore';
import noImageIcon from 'assets/images/no-image.svg';
import { NOTIFICATIONS_CATEGORIES, NOTIFICATIONS_TYPES } from 'entities/Notification';
import { useInView, useMedia } from 'shared/hooks';
import { ELanguages } from 'shared/typings';
import { formatDate } from 'shared/utils';

import { Notification } from '../../api/codegen.api';
import { IMPORTANT_TYPES } from '../../consts/importantTypes';
import { ENotificationTypes, IExtra } from '../../model/types';

import styles from './NotificationCard.module.scss';

interface INotificationCardProps extends HTMLAttributes<HTMLDivElement> {
	notification: Notification;
	size?: 's' | 'm';
	appendReadNotification?: (id: number) => void;
}

const intersectionOptions = {
	threshold: 1.0,
	triggerOnce: true,
};

export const NotificationCard: FC<INotificationCardProps> = ({
	className,
	notification,
	size = 'm',
	appendReadNotification,
	...props
}) => {
	const isMobile = useMedia();
	const { i18n } = useTranslation();
	const { readNotifications } = useSelector((s: RootState) => s.notificationsSlice);
	const { id, read, type, offer, source, icon, category, created_at } = notification;
	const isRead = read || readNotifications[id];

	const cardRef = useRef(null);
	const inView = useInView(cardRef, intersectionOptions);

	useEffect(() => {
		if (inView && !isRead) {
			appendReadNotification?.(id);
		}
	}, [inView]); //eslint-disable-line

	const textSizes = {
		title: {
			className: cn(styles.title, {
				[styles.titleSmall]: size === 's' || isMobile,
			}),
		},
		description: {
			className: cn(styles.description, {
				[styles.descriptionSmall]: size === 's' || isMobile,
			}),
		},
		info: {
			className: cn(styles.info, {
				[styles.infoSmall]: size === 's' || isMobile,
			}),
		},
	};

	const sizeClassName = size === 's' || isMobile ? 's' : 'm';

	const extraFields = NOTIFICATIONS_TYPES[type]?.extra;
	const descriptionExtra = extraFields.map((field) => notification?.extra?.[field as keyof IExtra]?.toString());

	const isCustom = type === ENotificationTypes.CUSTOM;
	const customNotification = notification?.extra?.[i18n.language as ELanguages] || notification?.extra?.[ELanguages.EN];

	return (
		<div
			{...props}
			ref={cardRef}
			className={cn(className, styles.notificationCard, styles[sizeClassName], {
				[styles.important]: IMPORTANT_TYPES?.[type],
				[styles.read]: isRead,
			})}>
			<div className={styles.left}>
				<div className={styles.icon}>
					<img src={icon || noImageIcon} alt="notification_icon" />
				</div>
				<div className={styles.dot}></div>
			</div>
			<div className={styles.right}>
				<Typography.Text className={textSizes.title.className} type={isRead ? 'secondary' : undefined}>
					{isCustom && customNotification?.title}
					{!isCustom && (NOTIFICATIONS_TYPES[type]?.title(offer, source) || '')}
				</Typography.Text>

				<Typography.Text className={textSizes.description.className} type="secondary">
					{isCustom && (
						<span
							dangerouslySetInnerHTML={
								customNotification?.body ? { __html: customNotification.body } : undefined
							}></span>
					)}
					{!isCustom && (NOTIFICATIONS_TYPES[type]?.text(offer, source, descriptionExtra) || '')}
				</Typography.Text>

				<Typography.Text className={textSizes.info.className} type="secondary">
					<span>{formatDate(new Date(created_at), 'long')}</span>
					<span>•</span>
					<span>{NOTIFICATIONS_CATEGORIES[category]?.name || category}</span>
				</Typography.Text>

				{/*{false && (*/}
				{/*	<div className={styles.link}>*/}
				{/*		<Typography.Link href="#" className={styles.brandLink}>*/}
				{/*			MMP Link*/}
				{/*		</Typography.Link>*/}
				{/*	</div>*/}
				{/*)}*/}
			</div>
		</div>
	);
};
